// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Billing hub page route.
 *
 * Provides a navigation map with live metric cards for billing operations:
 * billing plans, charges, and invoices in a three-column grid, each bucketed
 * per currency. This is the default landing page when clicking "Billing" in
 * the nav.
 */
import {
  BillingSummary,
  CurrencyChargeSummary,
  CurrencyInvoiceSummary,
  CurrencyPlanSummary,
} from '@/billing/api/types';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { AGING_BUCKET_VALUES } from '@/billing/components/invoice-status-constants';
import { useBillingSummary } from '@/billing/hooks/use-billing-summary';
import { Card } from '@/components/card';
import { PageContent } from '@/components/page-content';
import {
  MetricDivider,
  MetricTable,
  MetricTableRow,
  PrimaryMetric,
  SummaryCard,
} from '@/components/summary-card';
import {
  ArrowForwardRounded,
  CandlestickChartOutlined,
  DescriptionOutlined,
  FactCheckOutlined,
  PivotTableChartOutlined,
  ReceiptLongOutlined,
  TrendingDownOutlined,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { formatNumber } from '@/utilities/number';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Decimal } from 'decimal.js';
import { Suspense } from 'react';

export const Route = createFileRoute('/_app/billing/')({
  component: BillingRoute,
});

// Builds a per-currency breakdown row, formatting the bare amount with the
// bucket's own ISO code.
function toCurrencyTableRow(
  label: string,
  count: number,
  amount: string,
  currencyCode: IsoCurrencyCode,
  color?: string
): MetricTableRow {
  return {
    label,
    count: formatNumber(count),
    amount: formatAmountWithCurrency(amount, currencyCode),
    color,
  };
}

/**
 * Renders the billing hub page with metric cards.
 */
function BillingRoute() {
  return (
    <PageContent>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Billing
      </Typography>
      <Suspense fallback={<BillingSummaryCardsSkeleton />}>
        <BillingSummaryCards />
      </Suspense>

      {/* Reports section */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Reports
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card title="Collections">
            <Stack
              component={Link}
              to="/billing/aging"
              direction="row"
              alignItems="center"
              margin={1}
              padding={1}
              borderRadius={1}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <TrendingDownOutlined sx={{ color: 'text.secondary' }} />
              <Stack marginLeft={2}>
                <Typography variant="subtitle2">A/R aging report</Typography>
                <Typography variant="body2" color="text.secondary">
                  View accounts receivable by aging bucket with overdue
                  breakdown and carrying costs
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card title="Billing analysis">
            <Stack
              component={Link}
              to="/billing/lifecycle"
              direction="row"
              alignItems="center"
              margin={1}
              padding={1}
              borderRadius={1}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <CandlestickChartOutlined sx={{ color: 'text.secondary' }} />
              <Stack marginLeft={2}>
                <Typography variant="subtitle2">Billing lifecycle</Typography>
                <Typography variant="body2" color="text.secondary">
                  Analyze average days to send and average days to collect
                  across your billing pipeline
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card title="Billing detail">
            <Stack
              component={Link}
              to="/billing/invoice-line-items"
              direction="row"
              alignItems="center"
              margin={1}
              padding={1}
              borderRadius={1}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <PivotTableChartOutlined sx={{ color: 'text.secondary' }} />
              <Stack marginLeft={2}>
                <Typography variant="subtitle2">Invoice line items</Typography>
                <Typography variant="body2" color="text.secondary">
                  View every invoice line item with service period and billable
                  date for billing review and reconciliation
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </PageContent>
  );
}

function BillingSummaryCards() {
  const { data: summary } = useBillingSummary();

  // Drive all three cards from one currency order so the per-currency sections
  // line up across the columns. Every card renders every currency, zero-filled
  // where it has no data.
  const currencies = orderedCurrencies(summary);
  const planBuckets = currencies.map((currencyCode) =>
    bucketFor(summary.billingPlans.byCurrency, currencyCode, zeroPlanBucket)
  );
  const chargeBuckets = currencies.map((currencyCode) =>
    bucketFor(summary.charges.byCurrency, currencyCode, zeroChargeBucket)
  );
  const invoiceBuckets = currencies.map((currencyCode) =>
    bucketFor(summary.invoices.byCurrency, currencyCode, zeroInvoiceBucket)
  );

  return (
    <Grid container spacing={3}>
      {/* Billing plans */}
      <Grid item xs={12} md={4}>
        <BillingPlansCard byCurrency={planBuckets} />
      </Grid>

      {/* Charges */}
      <Grid item xs={12} md={4}>
        <ChargesCard byCurrency={chargeBuckets} />
      </Grid>

      {/* Invoices */}
      <Grid item xs={12} md={4}>
        <InvoicesCard byCurrency={invoiceBuckets} />
      </Grid>
    </Grid>
  );
}

// Union of currencies across all three cards, ordered by combined value
// (descending) with the ISO code as the deterministic tiebreak. One shared
// order keeps the per-currency sections aligned across columns.
function orderedCurrencies(summary: BillingSummary): IsoCurrencyCode[] {
  const weightByIso = new Map<IsoCurrencyCode, Decimal>();
  const add = (currencyCode: IsoCurrencyCode, amounts: string[]) => {
    const running = weightByIso.get(currencyCode) ?? new Decimal(0);
    weightByIso.set(
      currencyCode,
      amounts.reduce((total, amount) => total.plus(amount), running)
    );
  };
  summary.billingPlans.byCurrency.forEach((bucket) =>
    add(bucket.isoCode, [
      bucket.pendingAmount,
      bucket.confirmedAmount,
      bucket.activeAmount,
    ])
  );
  summary.charges.byCurrency.forEach((bucket) =>
    add(bucket.isoCode, [
      bucket.plannedAmount,
      bucket.billableAmount,
      bucket.invoicedAmount,
    ])
  );
  summary.invoices.byCurrency.forEach((bucket) =>
    add(bucket.isoCode, [
      bucket.draftAmount,
      bucket.currentAmount,
      bucket.overdueAmount,
    ])
  );
  const entries = Array.from(weightByIso.entries());
  entries.sort((left, right) => {
    if (!left[1].equals(right[1])) {
      return left[1].greaterThan(right[1]) ? -1 : 1;
    }
    return left[0] < right[0] ? -1 : 1;
  });
  return entries.map((entry) => entry[0]);
}

// Finds a currency's bucket, falling back to an all-zero bucket so every card
// shows every currency in the shared order even with no data for it.
function bucketFor<T extends { isoCode: IsoCurrencyCode }>(
  buckets: T[],
  currencyCode: IsoCurrencyCode,
  zero: (currencyCode: IsoCurrencyCode) => T
): T {
  return (
    buckets.find((bucket) => bucket.isoCode === currencyCode) ??
    zero(currencyCode)
  );
}

function zeroPlanBucket(currencyCode: IsoCurrencyCode): CurrencyPlanSummary {
  return {
    isoCode: currencyCode,
    pendingAmount: '0',
    pendingCount: 0,
    confirmedAmount: '0',
    confirmedCount: 0,
    activeAmount: '0',
    activeCount: 0,
  };
}

function zeroChargeBucket(
  currencyCode: IsoCurrencyCode
): CurrencyChargeSummary {
  return {
    isoCode: currencyCode,
    readyToInvoiceAmount: '0',
    plannedAmount: '0',
    plannedCount: 0,
    billableAmount: '0',
    billableCount: 0,
    invoicedAmount: '0',
    invoicedCount: 0,
  };
}

function zeroInvoiceBucket(
  currencyCode: IsoCurrencyCode
): CurrencyInvoiceSummary {
  return {
    isoCode: currencyCode,
    draftAmount: '0',
    draftCount: 0,
    currentAmount: '0',
    currentCount: 0,
    overdueAmount: '0',
    overdueCount: 0,
    paidRecentAmount: '0',
    paidRecentCount: 0,
  };
}

// Strips the default <a> styling from a TanStack Link nested under a headline
// value so the linked value matches the static one (no underline, inherit
// color). Applied to a wrapping element via a descendant selector.
const LINKED_VALUE_SX = {
  '& a': { textDecoration: 'none', color: 'inherit' },
} as const;

// Shared styling for the large single-currency headline value (matches
// PrimaryMetric's auto-rendered value so the linked variant looks identical).
const METRIC_VALUE_SX = {
  fontWeight: 600,
  textAlign: 'right',
} as const;

// Stops a click on a deep-linked headline value from also toggling the
// surrounding currency accordion in the multi-currency layout.
function stopAccordionToggle(event: React.MouseEvent) {
  event.stopPropagation();
}

// The card's headline metric for one currency, rendered in that currency's
// always-visible section header next to the ISO code. Colored to match the
// metric (warning for pending plans, info for ready-to-invoice charges). The
// value is a deep link to the matching filtered detail page.
function CurrencyMetricHeadline(props: {
  label: string;
  value: React.ReactNode;
  color: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      sx={LINKED_VALUE_SX}
    >
      <Typography variant="body2" color="text.secondary">
        {props.label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: props.color,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {props.value}
      </Typography>
    </Stack>
  );
}

// The charges hub card shows the ready-to-invoice (billable) headline above a
// Planned/Billable/On-invoice breakdown. With one currency the headline sits at
// the top of the card; with several it moves into each currency's collapsible
// header. There is no cross-currency total.
function ChargesCard(props: { byCurrency: CurrencyChargeSummary[] }) {
  const sole = props.byCurrency.length === 1 ? props.byCurrency[0] : null;
  return (
    <BillingHubCard
      icon={<ReceiptLongOutlined sx={{ color: 'text.secondary' }} />}
      title="Charges"
      linkTo="/billing/charges"
      linkLabel="View charges"
    >
      {sole && (
        <PrimaryMetric
          label="Ready to invoice"
          tooltip="Total amount of billable charges (ready to be added to invoices)"
          valueNode={
            <Box sx={LINKED_VALUE_SX}>
              <Typography
                variant="h3"
                sx={{ ...METRIC_VALUE_SX, color: 'info.main' }}
              >
                <Link to="/billing/charges" search={{ status: 'BILLABLE' }}>
                  {formatAmountWithCurrency(
                    sole.readyToInvoiceAmount,
                    sole.isoCode
                  )}
                </Link>
              </Typography>
            </Box>
          }
        />
      )}
      <MetricDivider>
        <PerCurrencySections
          buckets={props.byCurrency}
          emptyMessage="No charges yet"
          renderHeader={
            sole
              ? undefined
              : (bucket) => (
                  <CurrencyMetricHeadline
                    label="Ready to invoice"
                    color="info.main"
                    value={
                      <Link
                        to="/billing/charges"
                        search={{ status: 'BILLABLE' }}
                        onClick={stopAccordionToggle}
                      >
                        {formatAmountWithCurrency(
                          bucket.readyToInvoiceAmount,
                          bucket.isoCode
                        )}
                      </Link>
                    }
                  />
                )
          }
          renderBucket={(bucket) => (
            <MetricTable rows={chargeRowsForCurrency(bucket)} />
          )}
        />
      </MetricDivider>
    </BillingHubCard>
  );
}

// Match the lifecycle order used everywhere else (charges dashboard status
// toggle, hub card skeleton): Planned → Billable → On invoice.
function chargeRowsForCurrency(
  bucket: CurrencyChargeSummary
): MetricTableRow[] {
  return [
    toCurrencyTableRow(
      'Planned',
      bucket.plannedCount,
      bucket.plannedAmount,
      bucket.isoCode
    ),
    toCurrencyTableRow(
      'Billable',
      bucket.billableCount,
      bucket.billableAmount,
      bucket.isoCode
    ),
    toCurrencyTableRow(
      'On invoice',
      bucket.invoicedCount,
      bucket.invoicedAmount,
      bucket.isoCode
    ),
  ];
}

// Outstanding = everything issued-and-not-paid: Current + Overdue within a
// currency. Draft invoices haven't been issued so they don't count.
function currencyOutstanding(bucket: CurrencyInvoiceSummary): Decimal {
  return new Decimal(bucket.currentAmount).plus(bucket.overdueAmount);
}

// The invoices hub card shows the outstanding (current + overdue) headline
// above a Draft/Current/Overdue breakdown. With one currency the headline sits
// at the top of the card; with several it moves into each currency's
// collapsible header. There is no cross-currency total.
function InvoicesCard(props: { byCurrency: CurrencyInvoiceSummary[] }) {
  const sole = props.byCurrency.length === 1 ? props.byCurrency[0] : null;
  return (
    <BillingHubCard
      icon={<DescriptionOutlined sx={{ color: 'text.secondary' }} />}
      title="Invoices"
      linkTo="/billing/invoices"
      linkLabel="View invoices"
    >
      {sole && (
        <PrimaryMetric
          label="Total outstanding"
          tooltip="Total unpaid balance across open invoices (current and overdue)"
          valueNode={
            <Box sx={LINKED_VALUE_SX}>
              <Typography
                variant="h3"
                sx={{ ...METRIC_VALUE_SX, color: 'text.primary' }}
              >
                <Link
                  to="/billing/invoices"
                  search={{ agingBuckets: AGING_BUCKET_VALUES }}
                >
                  {formatCurrencyValue({
                    amount: currencyOutstanding(sole),
                    isoCode: sole.isoCode,
                  })}
                </Link>
              </Typography>
            </Box>
          }
        />
      )}
      <MetricDivider>
        <PerCurrencySections
          buckets={props.byCurrency}
          emptyMessage="No invoices yet"
          renderHeader={
            sole
              ? undefined
              : (bucket) => (
                  <CurrencyMetricHeadline
                    label="Total outstanding"
                    color="text.primary"
                    value={
                      <Link
                        to="/billing/invoices"
                        search={{ agingBuckets: AGING_BUCKET_VALUES }}
                        onClick={stopAccordionToggle}
                      >
                        {formatCurrencyValue({
                          amount: currencyOutstanding(bucket),
                          isoCode: bucket.isoCode,
                        })}
                      </Link>
                    }
                  />
                )
          }
          renderBucket={(bucket) => (
            <MetricTable
              rows={invoiceRowsForCurrency(bucket)}
              amountHeader="Balance"
            />
          )}
        />
      </MetricDivider>
    </BillingHubCard>
  );
}

function invoiceRowsForCurrency(
  bucket: CurrencyInvoiceSummary
): MetricTableRow[] {
  const overdueColor = new Decimal(bucket.overdueAmount).greaterThan(0)
    ? 'error.main'
    : 'inherit';
  return [
    currencyTableRow('Draft', bucket.draftCount, bucket.draftAmount, bucket),
    currencyTableRow(
      'Current',
      bucket.currentCount,
      bucket.currentAmount,
      bucket
    ),
    currencyTableRow(
      'Overdue',
      bucket.overdueCount,
      bucket.overdueAmount,
      bucket,
      overdueColor
    ),
  ];
}

function currencyTableRow(
  label: string,
  count: number,
  amount: string,
  bucket: CurrencyInvoiceSummary,
  color?: string
): MetricTableRow {
  return {
    label,
    count: formatNumber(count),
    amount: formatCurrencyValue({
      amount: new Decimal(amount),
      isoCode: bucket.isoCode,
    }),
    color,
  };
}

// The billing-plans hub card shows the pending-plan count headline above a
// Pending/Confirmed/Active value breakdown. With one currency the headline sits
// at the top of the card; with several it moves into each currency's
// collapsible header. There is no cross-currency total.
function BillingPlansCard(props: { byCurrency: CurrencyPlanSummary[] }) {
  const sole = props.byCurrency.length === 1 ? props.byCurrency[0] : null;
  return (
    <BillingHubCard
      icon={<FactCheckOutlined sx={{ color: 'text.secondary' }} />}
      title="Billing plans"
      linkTo="/billing/plans"
      linkLabel="View billing plans"
    >
      {sole && (
        <PrimaryMetric
          label="Pending"
          tooltip="Number of billing plans that are pending (awaiting an activation date)"
          valueNode={
            <Box sx={LINKED_VALUE_SX}>
              <Typography
                variant="h3"
                sx={{
                  ...METRIC_VALUE_SX,
                  color:
                    sole.pendingCount > 0 ? 'warning.main' : 'text.primary',
                }}
              >
                <Link to="/billing/plans" search={{ status: 'PENDING' }}>
                  {formatNumber(sole.pendingCount)}
                </Link>
              </Typography>
            </Box>
          }
        />
      )}
      <MetricDivider>
        <PerCurrencySections
          buckets={props.byCurrency}
          emptyMessage="No billing plans yet"
          renderHeader={
            sole
              ? undefined
              : (bucket) => (
                  <CurrencyMetricHeadline
                    label="Pending"
                    color={
                      bucket.pendingCount > 0 ? 'warning.main' : 'text.primary'
                    }
                    value={
                      <Link
                        to="/billing/plans"
                        search={{ status: 'PENDING' }}
                        onClick={stopAccordionToggle}
                      >
                        {formatNumber(bucket.pendingCount)}
                      </Link>
                    }
                  />
                )
          }
          renderBucket={(bucket) => (
            <MetricTable
              rows={planRowsForCurrency(bucket)}
              amountHeader="Value"
            />
          )}
        />
      </MetricDivider>
    </BillingHubCard>
  );
}

function planRowsForCurrency(bucket: CurrencyPlanSummary): MetricTableRow[] {
  return [
    toCurrencyTableRow(
      'Pending',
      bucket.pendingCount,
      bucket.pendingAmount,
      bucket.isoCode
    ),
    toCurrencyTableRow(
      'Confirmed',
      bucket.confirmedCount,
      bucket.confirmedAmount,
      bucket.isoCode
    ),
    toCurrencyTableRow(
      'Active',
      bucket.activeCount,
      bucket.activeAmount,
      bucket.isoCode
    ),
  ];
}

function BillingSummaryCardsSkeleton() {
  const skeletonChargeRows: MetricTableRow[] = [
    { label: 'Planned', count: '', amount: '' },
    { label: 'Billable', count: '', amount: '' },
    { label: 'On invoice', count: '', amount: '' },
  ];
  const skeletonInvoiceRows: MetricTableRow[] = [
    { label: 'Draft', count: '', amount: '' },
    { label: 'Current', count: '', amount: '' },
    { label: 'Overdue', count: '', amount: '' },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <BillingHubCard
          icon={<FactCheckOutlined sx={{ color: 'text.secondary' }} />}
          title="Billing plans"
          linkTo="/billing/plans"
          linkLabel="View billing plans"
        >
          <MetricDivider>
            <PrimaryMetric label="Pending" value="" isLoading />
            <MetricTable
              rows={[
                { label: 'Pending', count: '', amount: '' },
                { label: 'Confirmed', count: '', amount: '' },
                { label: 'Active', count: '', amount: '' },
              ]}
              isLoading
              amountHeader="Value"
            />
          </MetricDivider>
        </BillingHubCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <BillingHubCard
          icon={<ReceiptLongOutlined sx={{ color: 'text.secondary' }} />}
          title="Charges"
          linkTo="/billing/charges"
          linkLabel="View charges"
        >
          <MetricDivider>
            <PrimaryMetric label="Ready to invoice" value="" isLoading />
            <MetricTable rows={skeletonChargeRows} isLoading />
          </MetricDivider>
        </BillingHubCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <BillingHubCard
          icon={<DescriptionOutlined sx={{ color: 'text.secondary' }} />}
          title="Invoices"
          linkTo="/billing/invoices"
          linkLabel="View invoices"
        >
          <MetricDivider>
            <PrimaryMetric label="Total outstanding" value="" isLoading />
            <MetricTable
              rows={skeletonInvoiceRows}
              isLoading
              amountHeader="Balance"
            />
          </MetricDivider>
        </BillingHubCard>
      </Grid>
    </Grid>
  );
}

/**
 * A billing hub card that wraps SummaryCard with a title header and a navigation link.
 * The card is clickable to navigate to the linked page.
 */
interface BillingHubCardProps {
  icon: React.ReactNode;
  title: string;
  linkTo: string;
  linkLabel: string;
  isLinkDisabled?: boolean;
  children: React.ReactNode;
}

function BillingHubCard(props: BillingHubCardProps) {
  return (
    <SummaryCard>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {props.icon}
            <Typography variant="subtitle2" color="text.secondary">
              {props.title}
            </Typography>
          </Stack>
          {props.isLinkDisabled ? (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontStyle: 'italic' }}
            >
              Coming soon
            </Typography>
          ) : (
            <Button
              component={Link}
              to={props.linkTo}
              size="small"
              endIcon={<ArrowForwardRounded fontSize="small" />}
              sx={{ textTransform: 'none' }}
            >
              {props.linkLabel}
            </Button>
          )}
        </Stack>
        {props.children}
      </Stack>
    </SummaryCard>
  );
}
